/**
 * ChatBubble — floating action button for the AI chatbot.
 *
 * Animated with framer-motion:
 * - Entrance: bounces in from below on mount
 * - Idle: subtle breathing glow pulse
 * - Toggle: icon morphs between chat ↔ close with rotation
 * - Streaming: orbital ring animation
 * - Hover: scale up with elevated shadow
 */

import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useCockpitStore } from "../../store/CockpitStore";

export const ChatBubble: React.FC = () => {
  const { isOpen, toggle, isStreaming } = useCockpitStore();

  return (
    <motion.button
      onClick={toggle}
      className="fixed bottom-24 right-4 sm:bottom-6 sm:right-6 z-50 w-14 h-14 rounded-full flex items-center justify-center outline-none border-0 cursor-pointer"
      style={{
        background: isOpen
          ? "linear-gradient(135deg, #475569, #64748b)"
          : "linear-gradient(135deg, #025a94, #037ac7)",
      }}
      // Entrance animation
      initial={{ scale: 0, y: 40, opacity: 0 }}
      animate={{
        scale: 1,
        y: 0,
        opacity: 1,
        rotate: isOpen ? 90 : 0,
      }}
      transition={{
        type: "spring",
        stiffness: 260,
        damping: 20,
        rotate: { type: "spring", stiffness: 300, damping: 15 },
      }}
      whileHover={{ scale: 1.12 }}
      whileTap={{ scale: 0.9 }}
      aria-label={isOpen ? "Close chat" : "Open AI assistant"}
    >
      {/* Breathing glow ring (idle, not open) */}
      {!isOpen && !isStreaming && (
        <motion.span
          className="absolute inset-0 rounded-full"
          style={{ border: "2px solid rgba(2, 90, 148, 0.4)" }}
          animate={{
            scale: [1, 1.25, 1],
            opacity: [0.6, 0, 0.6],
          }}
          transition={{
            duration: 2.5,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      )}

      {/* Streaming orbital ring */}
      {isStreaming && !isOpen && (
        <>
          <motion.span
            className="absolute inset-[-3px] rounded-full"
            style={{
              border: "2px solid transparent",
              borderTopColor: "#22d3ee",
              borderRightColor: "#22d3ee",
            }}
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
          <motion.span
            className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-cyan-400"
            animate={{
              scale: [1, 1.3, 1],
              opacity: [1, 0.6, 1],
            }}
            transition={{ duration: 0.8, repeat: Infinity }}
          />
        </>
      )}

      {/* Shadow layers */}
      <motion.span
        className="absolute inset-0 rounded-full pointer-events-none"
        animate={{
          boxShadow: isOpen
            ? "0 4px 15px rgba(100, 116, 139, 0.3)"
            : isStreaming
              ? "0 4px 25px rgba(34, 211, 238, 0.4), 0 0 40px rgba(34, 211, 238, 0.15)"
              : "0 4px 20px rgba(2, 90, 148, 0.35), 0 0 30px rgba(3, 122, 199, 0.1)",
        }}
        transition={{ duration: 0.4 }}
      />

      {/* Icon with crossfade */}
      <AnimatePresence mode="wait" initial={false}>
        {isOpen ? (
          <motion.svg
            key="close"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.2}
            className="w-6 h-6 text-white relative z-10"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </motion.svg>
        ) : (
          <motion.svg
            key="chat"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="w-6 h-6 text-white relative z-10"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            <path d="M4.913 2.658c2.075-.27 4.19-.408 6.337-.408 2.147 0 4.262.139 6.337.408 1.922.25 3.291 1.861 3.405 3.727a4.403 4.403 0 00-1.032-.211 50.89 50.89 0 00-8.42 0c-2.358.196-4.04 2.19-4.04 4.434v4.286a4.47 4.47 0 002.433 3.984L7.28 21.53A.75.75 0 016 21v-4.03a48.527 48.527 0 01-1.087-.128C2.905 16.58 1.5 14.833 1.5 12.862V6.638c0-1.97 1.405-3.718 3.413-3.979z" />
            <path d="M15.75 7.5c-1.376 0-2.739.057-4.086.169C10.124 7.797 9 9.103 9 10.609v4.285c0 1.507 1.128 2.814 2.67 2.94 1.243.102 2.5.157 3.768.165l2.782 2.781a.75.75 0 001.28-.53v-2.39l.33-.026c1.542-.125 2.67-1.433 2.67-2.94v-4.286c0-1.505-1.125-2.811-2.664-2.94A49.392 49.392 0 0015.75 7.5z" />
          </motion.svg>
        )}
      </AnimatePresence>
    </motion.button>
  );
};

export default ChatBubble;
